import Zoom from '@mui/material/Zoom';

import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  Suspense,
  useCallback,
  useMemo,
  useReducer,
  useState,
  memo,
} from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { Dialog } from '@watershed/ui-core/components/Dialog';

import CommandPalettePaper, {
  CommandPalettePaperRef,
} from './CommandPalettePaper';

import CommandPaletteRoot from './CommandPaletteRoot';
import { CommandPalettePageContextProvider } from './CommandPaletteContext';
import {
  CommandPaletteHistoryEntry,
  CommandPalettePageContextShape,
  CommandPalettePush,
} from './types';
import ComandPaletteLoadingFallback from './ComandPaletteLoadingFallback';

interface CommandPaletteProps extends ComponentProps<typeof Dialog> {
  initialNavigationState: Array<CommandPaletteHistoryEntry<any>>;
}

function useLazyRef<T>(fn: () => T) {
  const ref = React.useRef<T | null>(null);

  if (ref.current === null) {
    ref.current = fn();
  }

  return ref.current;
}

export default memo(function CommandPalette({
  initialNavigationState,
  ...dialogProps
}: CommandPaletteProps) {
  const queryClient = useLazyRef(() => new QueryClient());

  const [stateKey, resetState] = useReducer((x) => x + 1, 0);

  const paperRef = React.useRef<CommandPalettePaperRef>(null);
  const [navigationStack, setNavigationStack] = useState<
    Array<CommandPaletteHistoryEntry<any>>
  >(initialNavigationState);

  const pageContext = useMemo(() => {
    const push: CommandPalettePush = (component, props) => {
      setNavigationStack((navigationStack) => {
        return [...navigationStack, { component, props: props ?? {} }];
      });
    };

    const pop = () => {
      setNavigationStack((navigationStack) => navigationStack.slice(0, -1));
    };

    const shake = () => {
      paperRef.current?.shake();
    };

    const close = () => {
      resetState();
      dialogProps.onClose();
    };

    return {
      location: navigationStack,
      push,
      pop,
      shake,
      close,
    } as CommandPalettePageContextShape;
  }, [dialogProps, navigationStack]);

  const currentLocation = navigationStack[navigationStack.length - 1];

  if (dialogProps.open && !currentLocation) {
    throw new Error(
      'Command Palette cannot be open with an empty navigation stack'
    );
  }

  const PageComponent = currentLocation?.component;
  const pageProps = currentLocation?.props;

  const PaperComponent = useCallback(
    (props: ComponentPropsWithoutRef<typeof CommandPalettePaper>) => (
      <CommandPalettePaper {...props} ref={paperRef} />
    ),
    []
  );

  return (
    <QueryClientProvider client={queryClient}>
      <CommandPalettePageContextProvider value={pageContext}>
        <Dialog
          key={stateKey}
          {...dialogProps}
          disableRestoreFocus
          TransitionComponent={Zoom}
          transitionDuration={150}
          contentSx={{
            padding: 0,
          }}
          PaperComponent={PaperComponent}
          slotProps={{
            backdrop: {
              invisible: true,
            },
          }}
          slots={{
            root: CommandPaletteRoot,
          }}
        >
          {dialogProps.open ? (
            <Suspense fallback={<ComandPaletteLoadingFallback />}>
              <PageComponent {...pageProps} key={navigationStack.length} />
            </Suspense>
          ) : null}
        </Dialog>
      </CommandPalettePageContextProvider>
    </QueryClientProvider>
  );
});
